import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon } from '@mui/icons-material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import TreeSelect from '@/components/common/TreeSelect';
import { transformToTreeStructure } from '@/utils/treeUtils';
import api from '@/services/api';
import rfidService, { ReaderPayload } from '@/services/rfidService';
import toast from '@/utils/toast';
import { parseApiError } from '@/utils/apiFieldErrors';

type LocationOption = {
  id: string;
  name: string;
  parent_location?: string | null;
};

const READER_TYPES = [
  { value: 'entry', label: 'Entry' },
  { value: 'exit', label: 'Exit' },
  { value: 'warehouse', label: 'Warehouse' },
  { value: 'handheld', label: 'Handheld' },
  { value: 'other', label: 'Other' },
  { value: 'unknown', label: 'Unknown' },
];

const EMPTY_READER: ReaderPayload = {
  name: '',
  device_id: '',
  description: '',
  scan_interval: 5,
  debounce_time: 2,
  threshold_time: 10,
  location: null,
  reader_type: 'other',
};

const ReaderForm: React.FC<{ isEdit?: boolean }> = ({ isEdit = false }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<ReaderPayload>(EMPTY_READER);
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const locationTree = useMemo(() => transformToTreeStructure(locations.map((location) => ({
    id: location.id,
    name: location.name,
    parent_location: location.parent_location ?? null,
  }))), [locations]);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await api.get('/master/locations/', { params: { page: 1, page_size: 1000, dropdown: true } });
        setLocations(response.data.results ?? response.data ?? []);
        if (isEdit && id) {
          const reader = await rfidService.getReaderById(id);
          setFormData({
            name: reader.name,
            device_id: reader.device_id,
            description: reader.description ?? '',
            scan_interval: reader.scan_interval,
            debounce_time: reader.debounce_time,
            threshold_time: reader.threshold_time,
            location: reader.location,
            reader_type: reader.reader_type,
          });
        }
      } catch (error) {
        console.error('Unable to load reader form', error);
        toast.error('Unable to load reader information');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, isEdit]);

  const setField = <K extends keyof ReaderPayload>(field: K, value: ReaderPayload[K]) => {
    setFormData((current) => ({ ...current, [field]: value }));
    setErrors((current) => {
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const validate = () => {
    const next: Record<string, string> = {};
    if (!formData.name.trim()) next.name = 'Reader name is required';
    if (!formData.device_id.trim()) next.device_id = 'Device ID is required';
    if (!formData.location) next.location = 'Location is required';
    if (!formData.reader_type) next.reader_type = 'Reader type is required';
    (['scan_interval', 'debounce_time', 'threshold_time'] as const).forEach((field) => {
      if (!Number.isFinite(formData[field]) || formData[field] < 0) next[field] = 'Enter a non-negative number';
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validate()) return;
    try {
      setSubmitting(true);
      if (isEdit && id) await rfidService.updateReader(id, formData);
      else await rfidService.createReader(formData);
      toast.success(`Reader ${isEdit ? 'updated' : 'created'} successfully`);
      navigate('/rfid/readers');
    } catch (error) {
      const { fieldErrors, message } = parseApiError(error);
      if (Object.keys(fieldErrors).length) setErrors((current) => ({ ...current, ...fieldErrors }));
      else toast.error(message || `Unable to ${isEdit ? 'update' : 'create'} reader`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>;

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} component={Link} to="/rfid/readers" sx={{ mr: 2 }}>Back</Button>
        <Typography variant="h4">{isEdit ? 'Edit Reader' : 'Create Reader'}</Typography>
      </Box>
      <form onSubmit={submit}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Reader Details</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth required label="Reader Name" value={formData.name} onChange={(event) => setField('name', event.target.value)} error={Boolean(errors.name)} helperText={errors.name} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth required label="Device ID" value={formData.device_id} onChange={(event) => setField('device_id', event.target.value)} error={Boolean(errors.device_id)} helperText={errors.device_id} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={3} label="Description" value={formData.description ?? ''} onChange={(event) => setField('description', event.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField select fullWidth required label="Reader Type" value={formData.reader_type} onChange={(event) => setField('reader_type', event.target.value)} error={Boolean(errors.reader_type)} helperText={errors.reader_type}>
                  {READER_TYPES.map((type) => <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TreeSelect label="Location" items={locationTree} value={formData.location ?? ''} onChange={(value) => setField('location', value || null)} error={Boolean(errors.location)} helperText={errors.location || 'Select the reader installation location'} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Scan Settings</Typography>
            <Grid container spacing={3}>
              {([
                ['scan_interval', 'Scan Interval (seconds)'],
                ['debounce_time', 'Debounce Time (seconds)'],
                ['threshold_time', 'Threshold Time (seconds)'],
              ] as const).map(([field, label]) => (
                <Grid item xs={12} md={4} key={field}>
                  <TextField fullWidth type="number" inputProps={{ min: 0 }} label={label} value={formData[field]} onChange={(event) => setField(field, Number(event.target.value))} error={Boolean(errors[field])} helperText={errors[field]} />
                </Grid>
              ))}
            </Grid>
            <Box display="flex" justifyContent="flex-end" mt={3} gap={1}>
              <Button component={Link} to="/rfid/readers">Cancel</Button>
              <Button type="submit" variant="contained" startIcon={<SaveIcon />} disabled={submitting}>{submitting ? 'Saving…' : 'Save Reader'}</Button>
            </Box>
          </CardContent>
        </Card>
      </form>
    </Box>
  );
};

export default ReaderForm;
